import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, Link2, X, Loader2 } from 'lucide-react';
import { useAppContext } from '../../../context/AppContext';
import { distributionService } from '../../../services/distributionService';
import MantleDataGrid, { gcell } from '../../../components/MantleDataGrid';
import PickerPopover from '../../../components/PickerPopover';
import SearchSelect from '../../../components/SearchSelect';
import { PERIODS } from '../../../utils/financialPeriods';
import usePeriodDrilldown, { periodColumns, periodsOfCol, periodHeaderToggle, aggPeriods } from '../../../hooks/usePeriodDrilldown';

// =============================================================================
// Warehousing > Operational Drivers
//
// The operational quantities a site runs at, per period: pallets moved, square
// metres occupied, kWh burned, hours run. Variable running costs multiply a
// rate by one of these, so this page is upstream of that one and deliberately
// separate from it — two dense grids stacked on one screen was the reason the
// original mockup was hard to read.
//
// A driver's twelve numbers come from one of two places:
//   MANUAL            typed here.
//   PRODUCTION/SALES  summed live from the volume plan. Those cells are
//                     read-only and are NOT stored — the server derives them on
//                     every read (vw_bud_driver_values), so they cannot drift
//                     behind an edit to volumes.
//
// The link is only as good as the units. Volume rows are held in each SKU's own
// UoM, so summing them into "pallets" requires the item master to say what a
// pallet of that SKU is (md_stock_codes.alt_uom_*). A SKU with no such unit
// drops out of the sum rather than contributing an unconverted quantity — the
// count reads low, which someone questions, instead of reading wrong.
//
// TOTAL vs AVG: both are shown because neither is right for every driver. A
// flow (pallets, kWh) accumulates over the year; a level (m², headcount,
// inventory value) does not — twelve months of 5 000 m² is 5 000 m², not
// 60 000. Rather than tag each driver, the page shows both and lets the reader
// use the one that means something.
// =============================================================================

const SOURCE_OPTIONS = [
  { code: 'MANUAL', label: 'Manual', description: 'Typed here, per period' },
  { code: 'PRODUCTION_VOLUME', label: 'Production volumes', description: 'Summed from the production plan' },
  { code: 'SALES_VOLUME', label: 'Sales volumes', description: 'Summed from the sales / sell-out plan' },
];
const SOURCE_LABEL = Object.fromEntries(SOURCE_OPTIONS.map((s) => [s.code, s.label]));

// Read-only cells are shaded so a derived number never looks like an input.
const T_LINKED = { bgCell: 'rgba(100, 116, 139, 0.10)', textDark: '#64748b' };
const T_AGG = { bgCell: 'rgba(100, 116, 139, 0.08)', baseFontStyle: '600 12px' };

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// ---- Volume link dialog ------------------------------------------------------
// Only reachable for a linked driver. Three questions: what unit to sum in,
// which SKUs count, and any final scaling.
const LinkModal = ({ line, driverName, uoms, productClasses, stockGroups, onApply, onClose }) => {
  const [uom, setUom] = useState(line.volume_uom || '');
  const [classes, setClasses] = useState(line.volume_filter?.product_class || []);
  const [groups, setGroups] = useState(line.volume_filter?.stock_group || []);
  const [factor, setFactor] = useState(String(line.conversion_factor ?? 1));

  const factorNum = Number(factor);
  const problem = !Number.isFinite(factorNum) || factorNum <= 0
    ? 'The conversion factor must be greater than zero.' : '';

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const apply = () => {
    if (problem) return;
    const filter = {};
    if (classes.length) filter.product_class = classes;
    if (groups.length) filter.stock_group = groups;
    onApply({ volume_uom: uom || null, volume_filter: filter, conversion_factor: factorNum });
  };

  const chip = (active) =>
    `px-2 py-1 text-xs rounded-md border transition-colors ${active
      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/50'
      : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg max-h-full overflow-y-auto rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="sticky top-0 px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Volume link — {driverName}</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              How the {SOURCE_LABEL[line.value_source]?.toLowerCase()} plan is turned into this driver.
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <p className="m-0 mb-1 text-xs font-semibold text-slate-600 dark:text-slate-300">Sum volumes in</p>
            <SearchSelect className="w-56" value={uom} onChange={setUom} options={uoms.map((u) => ({ code: u }))}
              clearable clearLabel="(SKU base units)" placeholder="(SKU base units)" emptyText="No unit matches." />
            <p className="m-0 mt-1 text-[11px] text-slate-400">
              Converted per SKU from the item master. A SKU with no factor for this unit is left out of the sum.
            </p>
          </div>

          <div>
            <p className="m-0 mb-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
              Product classes <span className="font-normal text-slate-400">— none selected means all</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {productClasses.length === 0
                ? <span className="text-xs text-slate-400">No product classes on the item master.</span>
                : productClasses.map((c) => (
                    <button key={c} type="button" className={chip(classes.includes(c))}
                      onClick={() => toggle(classes, setClasses, c)}>{c}</button>
                  ))}
            </div>
          </div>

          <div>
            <p className="m-0 mb-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
              Stock groups <span className="font-normal text-slate-400">— none selected means all</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {stockGroups.length === 0
                ? <span className="text-xs text-slate-400">No stock groups on the item master.</span>
                : stockGroups.map((g) => (
                    <button key={g} type="button" className={chip(groups.includes(g))}
                      onClick={() => toggle(groups, setGroups, g)}>{g}</button>
                  ))}
            </div>
          </div>

          <div>
            <p className="m-0 mb-1 text-xs font-semibold text-slate-600 dark:text-slate-300">Then multiply by</p>
            <input type="number" step="any" value={factor} onChange={(e) => setFactor(e.target.value)}
              className="w-32 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400" />
            <p className="m-0 mt-1 text-[11px] text-slate-400">
              For anything the unit conversion does not cover — handling passes per pallet, double-touch picks. Leave at 1 for none.
            </p>
          </div>
        </div>

        <div className="px-5 pb-5 flex items-center justify-between gap-3">
          <p className="m-0 text-xs text-red-600 dark:text-red-400">{problem}</p>
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
              Cancel
            </button>
            <button type="button" onClick={apply} disabled={!!problem}
              className={`px-4 py-2 text-sm font-semibold rounded-md text-white ${problem ? 'bg-slate-400 dark:bg-slate-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Page --------------------------------------------------------------------
const OperationalDrivers = () => {
  const { selectedVersion, selectedSite, fyStartMonth } = useAppContext();

  const [lines, setLines] = useState([]);
  const [values, setValues] = useState({});          // { driver_line_id: { period: qty } }
  const [catalogue, setCatalogue] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [productClasses, setProductClasses] = useState([]);
  const [stockGroups, setStockGroups] = useState([]);

  const [dirtyLines, setDirtyLines] = useState({});  // { driver_line_id: true }
  const [dirtyValues, setDirtyValues] = useState({});// { `${id}:${period}`: true }
  const [picker, setPicker] = useState(null);        // source picker
  const [linkFor, setLinkFor] = useState(null);      // driver_line_id
  const [addCode, setAddCode] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);

  // Writes are keyed to one site; without one this is a read-only overview.
  const ready = !!selectedVersion;
  const canEdit = ready && !!selectedSite;

  const driverName = useCallback((code) =>
    catalogue.find((d) => d.code === code)?.description || code, [catalogue]);
  const driverUom = useCallback((code) =>
    catalogue.find((d) => d.code === code)?.driver_uom || '', [catalogue]);

  const load = useCallback(async () => {
    if (!ready) { setLines([]); setValues({}); return; }
    setIsLoading(true); setError(null);
    try {
      const res = await distributionService.getDrivers();
      if (!res.success) throw new Error(res.message);
      setLines(res.data.lines || []);
      const byLine = {};
      for (const v of res.data.values || []) {
        (byLine[v.driver_line_id] ||= {})[v.financial_period] = v.driver_qty;
      }
      setValues(byLine);
      setCatalogue(res.data.catalogue || []);
      setUoms(res.data.uoms || []);
      setProductClasses(res.data.productClasses || []);
      setStockGroups(res.data.stockGroups || []);
      setDirtyLines({}); setDirtyValues({});
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load operational drivers.');
      setLines([]);
    } finally { setIsLoading(false); }
  }, [ready]);

  useEffect(() => { load(); }, [load, selectedSite]);

  const linesRef = useRef([]);
  linesRef.current = lines;

  // Only offer drivers the site has not already taken.
  const addOptions = useMemo(() => {
    const taken = new Set(lines.filter((l) => l.site_code === selectedSite).map((l) => l.driver_code));
    return catalogue.filter((d) => !taken.has(d.code))
      .map((d) => ({ code: d.code, name: `${d.description}${d.driver_uom ? ` (${d.driver_uom})` : ''}` }));
  }, [catalogue, lines, selectedSite]);

  // ---- Grid --------------------------------------------------------------------
  const allSites = !selectedSite;
  const drill = usePeriodDrilldown(PERIODS);

  const gcols = useMemo(() => ([
    ...(allSites ? [{ id: 'site', title: 'Site', width: 120 }] : []),
    { id: 'driver', title: 'Cost Driver', width: 210 },
    { id: 'uom', title: 'UoM', width: 76 },
    { id: 'source', title: 'Source', width: 150 },
    { id: 'link', title: 'Link', width: 190 },
    // Periods drill down — Full Year → quarters → months, same as OPEX entry.
    // Driver quantities are counts, so a group column SUMS them.
    ...periodColumns(drill, fyStartMonth, { monthWidth: 82, groupWidth: 118 }),
    { id: 'total', title: 'Total', width: 110 },
    { id: 'avg', title: 'Avg / mth', width: 100 },
    ...(canEdit ? [{ id: 'del', title: '', width: 36 }] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [allSites, fyStartMonth, canEdit, drill.visibleCols, drill.yearExpanded, drill.expandedQuarters]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const qtyOf = useCallback((id, p) => values[id]?.[p] ?? 0, [values]);

  // A one-line summary of the link, so the common case needs no dialog to read.
  const linkSummary = useCallback((l) => {
    if (l.value_source === 'MANUAL') return '—';
    const bits = [l.volume_uom ? `in ${l.volume_uom}` : 'in base units'];
    const f = l.volume_filter || {};
    const filters = (f.product_class?.length || 0) + (f.stock_group?.length || 0);
    bits.push(filters ? `${filters} filter${filters === 1 ? '' : 's'}` : 'all SKUs');
    if (Number(l.conversion_factor) !== 1) bits.push(`x${l.conversion_factor}`);
    return bits.join(' · ');
  }, []);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const l = linesRef.current[row];
    if (!l) return gcell.text('', { readonly: true });

    const linked = l.value_source !== 'MANUAL';
    // In All Sites the page is a read-only overview: rows span sites, and a
    // write needs one concrete site to land on.
    const locked = linked || !canEdit;

    switch (id) {
      case 'site': return gcell.text(l.site_code, { readonly: true });
      case 'driver': return gcell.text(driverName(l.driver_code), { readonly: true });
      case 'uom': return gcell.text(driverUom(l.driver_code), { readonly: true });
      case 'source': return gcell.text(SOURCE_LABEL[l.value_source] || l.value_source, { readonly: true });
      case 'link': return gcell.text(linkSummary(l), { readonly: true, theme: linked ? undefined : T_LINKED });
      case 'total': {
        const t = PERIODS.reduce((a, p) => a + qtyOf(l.driver_line_id, p), 0);
        return gcell.number(t, { readonly: true, display: fmt(t), theme: T_AGG });
      }
      case 'avg': {
        const t = PERIODS.reduce((a, p) => a + qtyOf(l.driver_line_id, p), 0) / 12;
        return gcell.number(t, { readonly: true, display: fmt(t), theme: T_AGG });
      }
      case 'del': return gcell.text('✕', { readonly: true, theme: { textDark: '#94a3b8' } });
      default: {
        const ps = periodsOfCol(gcols, id);
        if (!ps) return gcell.text('', { readonly: true });
        const v = aggPeriods(ps, (p) => qtyOf(l.driver_line_id, p));
        // A quarter or the full year is an aggregate: read-only, whatever the
        // line's own source is.
        const agg = ps.length > 1;
        return gcell.number(v, {
          readonly: locked || agg,
          display: fmt(v),
          theme: agg ? T_AGG : (locked ? T_LINKED : undefined),
        });
      }
    }
  }, [colIds, gcols, canEdit, driverName, driverUom, linkSummary, qtyOf]);

  const onCellEdited = useCallback(([col, row], newValue) => {
    const id = colIds[col];
    const l = linesRef.current[row];
    if (!l || l.value_source !== 'MANUAL' || !canEdit) return;
    const ps = periodsOfCol(gcols, id);
    if (ps?.length !== 1) return; // months only — a quarter is not typed into
    const p = ps[0];
    const n = Number(newValue.data);
    if (!Number.isFinite(n)) return;
    setValues((prev) => ({ ...prev, [l.driver_line_id]: { ...(prev[l.driver_line_id] || {}), [p]: n } }));
    setDirtyValues((prev) => ({ ...prev, [`${l.driver_line_id}:${p}`]: true }));
  }, [colIds, gcols, canEdit]);

  const removeLine = useCallback(async (l) => {
    if (!window.confirm(`Remove ${driverName(l.driver_code)} from this site? Its captured quantities go with it.`)) return;
    setError(null);
    try {
      await distributionService.deleteDriver(l.driver_line_id);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to remove this driver.');
    }
  }, [driverName, load]);

  const onCellClicked = useCallback(([col, row], event) => {
    const id = colIds[col];
    const l = linesRef.current[row];
    if (!l || !canEdit) return;
    const b = event?.bounds;
    const pos = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };
    if (id === 'source') setPicker({ id: l.driver_line_id, ...pos });
    else if (id === 'link' && l.value_source !== 'MANUAL') setLinkFor(l.driver_line_id);
    else if (id === 'del') removeLine(l);
  }, [colIds, canEdit, removeLine]);

  const patchLine = useCallback((lineId, fields) => {
    setLines((prev) => prev.map((l) => (l.driver_line_id === lineId ? { ...l, ...fields } : l)));
    setDirtyLines((prev) => ({ ...prev, [lineId]: true }));
  }, []);

  // Switching to a link does not clear the stored manual numbers — the server
  // stops reading them, and they are still there if the user switches back.
  const applySource = useCallback((lineId, source) => {
    patchLine(lineId, { value_source: source });
    setPicker(null);
  }, [patchLine]);

  // ---- Add / save --------------------------------------------------------------
  const addDriver = async () => {
    if (!addCode || isAdding) return;
    setIsAdding(true); setError(null);
    try {
      const res = await distributionService.addDriver({ driver_code: addCode });
      if (!res.success) throw new Error(res.message);
      setAddCode('');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to add this driver.');
    } finally { setIsAdding(false); }
  };

  const hasChanges = Object.keys(dirtyLines).length > 0 || Object.keys(dirtyValues).length > 0;

  const save = useCallback(async () => {
    if (!hasChanges || isSaving) return;
    setIsSaving(true); setError(null);
    try {
      const payloadLines = linesRef.current
        .filter((l) => dirtyLines[l.driver_line_id])
        .map((l) => ({
          driver_line_id: l.driver_line_id,
          value_source: l.value_source,
          volume_uom: l.volume_uom,
          volume_filter: l.volume_filter,
          conversion_factor: l.conversion_factor,
          notes: l.notes,
        }));
      const payloadValues = Object.keys(dirtyValues).map((k) => {
        const [driver_line_id, p] = k.split(':');
        return { driver_line_id, financial_period: Number(p), driver_qty: values[driver_line_id]?.[p] ?? 0 };
      });
      const res = await distributionService.saveDrivers({ lines: payloadLines, values: payloadValues });
      if (!res.success) throw new Error(res.message);
      // Reload rather than trust local state: a line switched to a volume link
      // gets its twelve numbers back from the server, not from this page.
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save operational drivers.');
    } finally { setIsSaving(false); }
  }, [hasChanges, isSaving, dirtyLines, dirtyValues, values, load]);

  const linkLine = lines.find((l) => l.driver_line_id === linkFor) || null;

  const periodBtn = 'px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors';

  const toolbar = useMemo(() => (
    <>
      <button type="button" className={periodBtn}
              onClick={drill.yearExpanded ? drill.collapseAll : drill.expandAll}>
        {drill.yearExpanded ? 'Collapse periods' : 'Expand periods'}
      </button>
      <button type="button" onClick={save} disabled={isSaving || !hasChanges}
        className={`px-3 py-1 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          hasChanges
            ? 'border-emerald-700 bg-emerald-600 text-white font-semibold hover:bg-emerald-700'
            : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
        {isSaving ? 'Saving…' : 'Save'}
      </button>
    </>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [isSaving, hasChanges, save, drill.yearExpanded, drill.collapseAll, drill.expandAll]);

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Warehousing · Operational Drivers
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          The quantities this site runs at, per period. Variable running costs are a rate times one of these,
          so set them here once and every expense keyed to a driver follows.
        </p>
        {error && <div className="mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
      </div>

      {ready && canEdit && (
        <div className="shrink-0 px-6 pb-3">
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>Add a driver</span>
              <SearchSelect className="w-72" value={addCode} onChange={setAddCode} options={addOptions}
                placeholder="Select a cost driver…" emptyText="Every driver is already on this site." />
            </div>
            <button type="button" onClick={addDriver} disabled={!addCode || isAdding}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white mb-0.5 ${
                !addCode || isAdding ? 'bg-slate-400 dark:bg-slate-600 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
              {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
            </button>
            <p className="m-0 ml-auto text-[11px] text-slate-400 max-w-md">
              The list comes from Master Data → Cost Drivers. Add your own there and it appears here without a code change.
            </p>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="max-h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
          {!ready ? (
            <div className="p-10 text-center text-sm text-slate-400">Select a budget version in the top bar to begin.</div>
          ) : isLoading ? (
            <div className="p-4 text-xs text-slate-400">Loading…</div>
          ) : lines.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">
              {canEdit
                ? 'No drivers on this site yet — add one above.'
                : 'No drivers captured. Select a site in the top bar to add them.'}
            </div>
          ) : (
            <MantleDataGrid
              gridId="dist-operational-drivers"
              fitHeight
              fitHeightPadding={12}
              columns={gcols}
              rowCount={lines.length}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onCellClicked={onCellClicked}
              onHeaderClicked={(col) => periodHeaderToggle(drill, colIds[col])}
              freezeColumns={allSites ? 3 : 2}
              hint={canEdit
                ? 'Click Full Year → quarters → months to drill into the periods. Click Source to switch between typing the numbers and linking them to the volume plan. Linked rows are shaded and read-only — edit the volumes instead. ✕ removes a driver.'
                : 'Click Full Year → quarters → months to drill into the periods. All Sites — read-only; select one site in the top bar to edit.'}
              toolbar={toolbar}
            />
          )}
        </div>
      </div>

      {picker && (
        <PickerPopover
          key={picker.id}
          anchor={picker}
          options={SOURCE_OPTIONS}
          value={lines.find((l) => l.driver_line_id === picker.id)?.value_source}
          onPick={(code) => applySource(picker.id, code)}
          onClose={() => setPicker(null)}
        />
      )}

      {linkLine && (
        <LinkModal
          line={linkLine}
          driverName={driverName(linkLine.driver_code)}
          uoms={uoms}
          productClasses={productClasses}
          stockGroups={stockGroups}
          onApply={(fields) => { patchLine(linkLine.driver_line_id, fields); setLinkFor(null); }}
          onClose={() => setLinkFor(null)}
        />
      )}

      <p className="shrink-0 px-6 pb-4 m-0 text-[11px] text-slate-400 flex items-center gap-1.5">
        <Link2 className="w-3 h-3" />
        Total suits a flow (pallets, kWh, hours); Avg / mth suits a level (floor area, headcount, inventory value).
        Linked drivers are recalculated on every load, so a change to the volume plan shows up here without a re-save.
      </p>
    </div>
  );
};

export default OperationalDrivers;
